import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import _ from 'lodash';
import {useMemo} from 'react';

import {useDeepMemo} from '../../../../../../hookUtils';
import {
  getCachedByKeyWithExpiry,
  setCacheByKeyWithExpiry,
  simpleHash,
} from '../../browserCacheUtils';
import {
  isValuelessOperator,
  makeDateFilter,
  makeMonthFilter,
  makeRawDateFilter,
} from '../../filters/common';
import {operationConverter} from '../common/tabularListViews/operators';
import {useWFHooks} from '../wfReactInterface/context';
import {Query} from '../wfReactInterface/traceServerClientInterface/query';
import {SortBy} from '../wfReactInterface/traceServerClientTypes';
import {
  CallFilter,
  CallSchema,
  UseCallsParams,
} from '../wfReactInterface/wfDataModelHooksInterface';
import {WFHighLevelCallFilter} from './callsTableFilter';

export const DEFAULT_FILTER_CALLS: GridFilterModel = {
  items: [],
  logicOperator: GridLogicOperator.And,
};

export interface UseCallsForQueryParams {
  entity: string;
  project: string;
  filter: WFHighLevelCallFilter;
  gridFilter: GridFilterModel;
  gridPage: GridPaginationModel;
  gridSort?: GridSortModel;
  expandColumns?: Set<string>;
  columns?: string[];
  options?: {
    includeTotalStorageSize?: boolean;
  };
  skip?: boolean;
}

export interface UseCallsForQueryResult {
  costsLoading: boolean;
  result: CallSchema[];
  metadataLoading: boolean;
  heavyIOLoading: boolean;
  total: number;
  refetch: () => void;
  storageSizeLoading: boolean;
  feedbackLoading: boolean;
  primaryError?: Error | null;
  costsError?: Error | null;
  storageSizeError?: Error | null;
  feedbackError?: Error | null;
}

/**
 * This Hook is responsible for bridging the gap between the CallsTable
 * component and the underlying data hooks. In particular, it takes a high level
 * filter, column filter, sort def, page def, and expanded columns and returns
 * the associated calls. Internally, we convert each of these data structures
 * from their higher level representation to the lower-level API representation.
 *
 * ! Note: the expandedColumns parameter is required to enable filtering calls
 * by object references, and/or to manually de-normalize refs in the server
 *
 * Moreover, we also handle extracting the counts for a given query which is used to
 * determine the total number of calls for pagination.
 *
 * @param params - The query parameters including entity, project, filters, pagination, etc.
 * @returns Object containing processed call data, loading states, and error information
 *
 * @example
 * const callsQuery = useCallsForQuery({
 *   entity: 'my-entity',
 *   project: 'my-project',
 *   filter: callsFilter,
 *   gridFilter: filterModel,
 *   gridPage: paginationModel,
 *   gridSort: sortModel,
 *   expandedColumns: expandedRefCols,
 *   columns: ['id', 'display_name'],
 *   options: { includeTotalStorageSize: true }
 * });
 */
export const useCallsForQuery = (
  params: UseCallsForQueryParams
): UseCallsForQueryResult => {
  const {
    entity,
    project,
    filter,
    gridFilter,
    gridPage,
    gridSort,
    expandColumns,
    columns,
    options,
    skip,
  } = params;
  const effectiveOffset = gridPage?.page * gridPage?.pageSize;
  const effectiveLimit = gridPage.pageSize;
  const {sortBy, lowLevelFilter, filterBy} = useFilterSortby(
    filter,
    gridFilter,
    gridSort
  );

  const newResult = useProgressivelyHydratedCallsForTableQuery({
    entity,
    project,
    filter: lowLevelFilter,
    limit: effectiveLimit,
    offset: effectiveOffset,
    sortBy,
    query: filterBy,
    columns: columns,
    expandColumns: expandColumns,
    skip: skip,
    refetchOnDelete: true,
    includeCosts: true,
    includeFeedback: true,
    includeStorageSize: false,
    includeTotalStorageSize: options?.includeTotalStorageSize ?? false,
  });

  return useMemo(() => {
    return {
      costsLoading: newResult.loadingStatus.costs,
      result: newResult.result.rows,
      metadataLoading: newResult.loadingStatus.metadata,
      heavyIOLoading: newResult.loadingStatus.heavyIO,
      total: newResult.result.total,
      refetch: newResult.refetch,
      storageSizeLoading: newResult.loadingStatus.storage,
      feedbackLoading: newResult.loadingStatus.feedback,
      primaryError: newResult.errorStatus.metadata,
      costsError: newResult.errorStatus.costs,
      storageSizeError: newResult.errorStatus.storage,
      feedbackError: newResult.errorStatus.feedback,
    };
  }, [newResult]);
};

type ProgressivelyHydratedCallsForTableQueryResult = {
  loadingStatus: {
    metadata: boolean;
    stats: boolean;
    heavyIO: boolean;
    feedback: boolean;
    costs: boolean;
    storage: boolean;
  };
  result: {
    rows: CallSchema[];
    total: number;
  };
  errorStatus: {
    metadata: Error | null;
    stats: Error | null;
    heavyIO: Error | null;
    feedback: Error | null;
    costs: Error | null;
    storage: Error | null;
  };
  refetch: () => void;
};

const METADATA_COLUMNS = [
  'id',
  'project_id',
  'op_name',
  'display_name',
  'trace_id',
  'parent_id',
  'thread_id',
  'turn_id',
  'started_at',
  'ended_at',
  'exception',
  'summary',
  'wb_user_id',
  'wb_run_id',
  'wb_run_step',
];

const HEAVY_IO_COLUMNS = ['id', `attributes`, 'inputs', 'output'];

const sortIsNonTrivial = (sortBy: SortBy[] | undefined) => {
  if (sortBy == null) {
    return false;
  }
  return _.some(
    sortBy,
    sort => sort.field.startsWith('inputs') || sort.field.startsWith('output')
  );
};

const queryIsNonTrivial = (query: Query | undefined) => {
  // Find all `$getField` operations in the query
  // Recursively look at all the properties of the query (going into arrays and objects)
  // Look for `$getField` keys.
  // If the getField's value starts with 'inputs' or 'output', return true.

  if (query == null) {
    return false;
  }

  const checkValue = (value: any): boolean => {
    // Base case: null or undefined
    if (value == null) {
      return false;
    }

    // If it's an object, check for $getField key
    if (typeof value === 'object') {
      // Check if this object has a $getField property
      if ('$getField' in value) {
        const fieldValue = value.$getField;
        if (typeof fieldValue === 'string') {
          if (
            fieldValue.startsWith('inputs') ||
            fieldValue.startsWith('output')
          ) {
            return true;
          }
        }
      }

      // Recursively check all properties (for objects) or elements (for arrays)
      if (Array.isArray(value)) {
        return value.some(item => checkValue(item));
      } else {
        return Object.values(value).some(val => checkValue(val));
      }
    }

    // Primitive values don't need further checking
    return false;
  };

  return checkValue(query);
};

/**
 * `useProgressivelyHydratedCallsForTableQuery` is a fairly sophisticated hook that is optimized for rendering
 * data for the calls table. It effectively replaces `useCallsForQuery`, which now is just an adapter to this hook.
 *
 * Specifically, we break down the query into 6 separate queries, each of which is responsible for fetching a different
 * aspect of the data. This is done to optimize the rendering performance of the calls table.
 *
 * Query 1: Get metadata for the calls
 * Query 2: Get the stats for the calls
 * Query 3: Get the heavy I/O & expanded columns for the calls
 * Query 4: Get feedback data for the calls
 * Query 5: Get cost data for the calls
 * Query 6: Get storage size data for the calls
 *
 * Query 1 and 2 are ran in parallel.
 * Query 3-6 are ran in parallel.
 * If the base query is non-trivial, we wait for Query 1 to complete before starting 3-6. However, if the base query is trivial,
 * we start 3-6 immediately.
 *
 * Finally, the results are merged and discrete loading states are returned.
 *
 * The net effect is that we return the metadata and counts as quickly as possible, then
 * progressively hydrate the heavy I/O, feedback, cost, and storage size data.
 */
const useProgressivelyHydratedCallsForTableQuery = (
  params: UseCallsParams
): ProgressivelyHydratedCallsForTableQueryResult => {
  const {useCalls, useCallsStats} = useWFHooks();
  const metadataQuery = useCalls({
    ...params,
    columns: METADATA_COLUMNS,
    expandColumns: undefined,
    includeCosts: false,
    includeFeedback: false,
    includeStorageSize: false,
    includeTotalStorageSize: false,
  });

  const statsQuery = useCallsStats({
    ...params,
    limit: undefined,
    includeTotalStorageSize: false,
    expandColumns: undefined,
  });

  const callIds = useMemo(() => {
    return metadataQuery.result?.map(call => call.callId) || [];
  }, [metadataQuery.result]);

  const supplementalParams: UseCallsParams = useMemo(() => {
    let paramsAreNonTrivial =
      queryIsNonTrivial(params.query) || sortIsNonTrivial(params.sortBy);

    if (paramsAreNonTrivial) {
      return {
        ...params,
        filter: {
          callIds,
        },
        limit: undefined,
        offset: undefined,
        sortBy: undefined,
        query: undefined,
        skip: params.skip || callIds.length === 0,
      };
    } else {
      return params;
    }
  }, [params, callIds]);

  const heavyIOQuery = useCalls({
    ...supplementalParams,
    columns: HEAVY_IO_COLUMNS,
    includeCosts: false,
    includeFeedback: false,
    includeStorageSize: false,
    includeTotalStorageSize: false,
  });

  const feedbackQuery = useCalls({
    ...supplementalParams,
    columns: ['id', 'summary'],
    expandColumns: undefined,
    includeCosts: false,
    includeFeedback: true,
    includeStorageSize: false,
    includeTotalStorageSize: false,
    skip: supplementalParams.skip || params.includeFeedback === false,
  });

  const costQuery = useCalls({
    ...supplementalParams,
    columns: ['id', 'summary'],
    expandColumns: undefined,
    includeCosts: true,
    includeFeedback: false,
    includeStorageSize: false,
    includeTotalStorageSize: false,
    skip: supplementalParams.skip || params.includeCosts === false,
  });

  const storageQuery = useCalls({
    ...supplementalParams,
    columns: ['id', 'total_storage_size_bytes'],
    expandColumns: undefined,
    includeCosts: false,
    includeFeedback: false,
    includeStorageSize: false,
    includeTotalStorageSize: true,
    skip: supplementalParams.skip || params.includeTotalStorageSize === false,
  });

  return useMemo(() => {
    // Hydrate the rows
    let rows: CallSchema[] = _.cloneDeep(metadataQuery.result ?? []);
    // build reverse map of callId to index
    const callIdToIndex = new Map<string, number>();
    rows.forEach((call, index) => {
      callIdToIndex.set(call.callId, index);
    });

    // Hydrate the heavy IO
    heavyIOQuery.result?.forEach(row => {
      const index = callIdToIndex.get(row.callId);
      if (index != null) {
        const targetTraceCall = rows[index].traceCall;
        const sourceTraceCall = row.traceCall;
        if (targetTraceCall != null && sourceTraceCall != null) {
          targetTraceCall.attributes = sourceTraceCall.attributes;
          targetTraceCall.inputs = sourceTraceCall.inputs;
          targetTraceCall.output = sourceTraceCall.output;
        }
      }
    });

    // Hydrate feedback data
    feedbackQuery.result?.forEach(row => {
      const index = callIdToIndex.get(row.callId);
      if (index != null) {
        const targetTraceCall = rows[index].traceCall;
        const sourceTraceCall = row.traceCall;
        if (
          targetTraceCall != null &&
          sourceTraceCall != null &&
          sourceTraceCall.summary
        ) {
          targetTraceCall.summary = _.merge(
            {},
            targetTraceCall.summary,
            sourceTraceCall.summary
          );
        }
      }
    });

    // Handle Feedback: reduce to latest feedback of each type
    rows = getFeedbackMerged(rows);

    // Hydrate cost data
    costQuery.result?.forEach(row => {
      const index = callIdToIndex.get(row.callId);
      if (index != null) {
        const targetTraceCall = rows[index].traceCall;
        const sourceTraceCall = row.traceCall;
        if (
          targetTraceCall != null &&
          sourceTraceCall != null &&
          sourceTraceCall.summary
        ) {
          targetTraceCall.summary = _.merge(
            {},
            targetTraceCall.summary,
            sourceTraceCall.summary
          );
        }
      }
    });

    // Hydrate storage data
    storageQuery.result?.forEach(row => {
      const index = callIdToIndex.get(row.callId);
      if (index != null) {
        const targetTraceCall = rows[index].traceCall;
        const sourceTraceCall = row.traceCall;
        if (targetTraceCall != null && sourceTraceCall != null) {
          targetTraceCall.total_storage_size_bytes =
            sourceTraceCall.total_storage_size_bytes;
        }
      }
    });

    // Calculate the total number of calls
    let total = 0;
    if (!statsQuery.loading && statsQuery.result != null) {
      total = statsQuery.result.count;
    } else {
      total = (params.offset ?? 0) + (metadataQuery.result?.length ?? 0);
    }

    return {
      loadingStatus: {
        metadata: metadataQuery.loading,
        stats: statsQuery.loading,
        heavyIO: heavyIOQuery.loading || metadataQuery.loading,
        feedback: feedbackQuery.loading || metadataQuery.loading,
        costs: costQuery.loading || metadataQuery.loading,
        storage: storageQuery.loading || metadataQuery.loading,
      },
      result: {
        rows: rows,
        total: total,
      },
      errorStatus: {
        metadata: metadataQuery.error ?? null,
        stats: statsQuery.error ?? null,
        heavyIO: heavyIOQuery.error ?? null,
        feedback: feedbackQuery.error ?? null,
        costs: costQuery.error ?? null,
        storage: storageQuery.error ?? null,
      },
      refetch: () => {
        metadataQuery.refetch();
        statsQuery.refetch();
        heavyIOQuery.refetch();
        feedbackQuery.refetch();
        costQuery.refetch();
        storageQuery.refetch();
      },
    };
  }, [
    heavyIOQuery,
    metadataQuery,
    feedbackQuery,
    costQuery,
    storageQuery,
    params.offset,
    statsQuery,
  ]);
};

export const useFilterSortby = (
  filter: WFHighLevelCallFilter,
  gridFilter: GridFilterModel,
  gridSort: GridSortModel | undefined
) => {
  const sortBy = useDeepMemo(
    useMemo(() => (gridSort ? getSortBy(gridSort) : []), [gridSort])
  );
  const lowLevelFilter: CallFilter = useMemo(() => {
    return convertHighLevelFilterToLowLevelFilter(filter);
  }, [filter]);
  const filterByRaw = useMemo(() => getFilterByRaw(gridFilter), [gridFilter]);
  const filterBy: Query | undefined = useMemo(
    () => getFilterBy(filterByRaw),
    [filterByRaw]
  );

  return {
    sortBy,
    lowLevelFilter,
    filterBy,
  };
};

export const getFilterByRaw = (
  gridFilter: GridFilterModel
): Query['$expr'] | undefined => {
  const completeItems = gridFilter.items.filter(
    item => item.value !== undefined || isValuelessOperator(item.operator)
  );

  const convertedItems = completeItems
    .map(operationConverter)
    .filter(item => item !== null) as Array<Query['$expr']>;

  if (convertedItems.length === 0) {
    return undefined;
  }

  if (convertedItems.length === 1) {
    return convertedItems[0];
  }

  const operation = gridFilter.logicOperator === 'or' ? '$or' : '$and'; // and is default

  return {
    [operation]: convertedItems,
  } as Query['$expr'];
};

const getSortBy = (gridSort: GridSortModel) => {
  return gridSort.map(sort => {
    return {
      field: sort.field,
      direction: sort.sort ?? 'asc',
    };
  });
};

const getFilterBy = (
  filterByRaw: Query['$expr'] | undefined
): Query | undefined => {
  if (filterByRaw === undefined) {
    return undefined;
  }
  return {$expr: filterByRaw} as Query;
};

export const convertHighLevelFilterToLowLevelFilter = (
  effectiveFilter: WFHighLevelCallFilter
): CallFilter => {
  return {
    traceRootsOnly: effectiveFilter.traceRootsOnly,
    opVersionRefs: effectiveFilter.opVersionRefs,
    inputObjectVersionRefs: effectiveFilter.inputObjectVersionRefs,
    outputObjectVersionRefs: effectiveFilter.outputObjectVersionRefs,
    parentIds: effectiveFilter.parentId
      ? [effectiveFilter.parentId]
      : undefined,
    runIds: effectiveFilter.runIds,
    callIds: effectiveFilter.callIds,
  };
};

// Warning: This is a lossy conversion, there are things we
// can represent in a low level filter that we cannot represent
// in a high level filter.
export const convertLowLevelFilterToHighLevelFilter = (
  lowLevelFilter: CallFilter
): WFHighLevelCallFilter => {
  const highLevelFilter: WFHighLevelCallFilter = {};
  if (lowLevelFilter.opVersionRefs) {
    highLevelFilter.opVersionRefs = lowLevelFilter.opVersionRefs;
  }
  if (lowLevelFilter.inputObjectVersionRefs) {
    highLevelFilter.inputObjectVersionRefs =
      lowLevelFilter.inputObjectVersionRefs;
  }
  if (lowLevelFilter.outputObjectVersionRefs) {
    highLevelFilter.outputObjectVersionRefs =
      lowLevelFilter.outputObjectVersionRefs;
  }
  if (lowLevelFilter.parentIds) {
    highLevelFilter.parentId = lowLevelFilter.parentIds[0];
  }
  if (lowLevelFilter.traceRootsOnly) {
    highLevelFilter.traceRootsOnly = lowLevelFilter.traceRootsOnly;
  }
  if (lowLevelFilter.runIds) {
    highLevelFilter.runIds = lowLevelFilter.runIds;
  }
  if (lowLevelFilter.callIds) {
    highLevelFilter.callIds = lowLevelFilter.callIds;
  }
  return highLevelFilter;
};

const getFeedbackMerged = (calls: CallSchema[]) => {
  // for each call, reduce all feedback to the latest feedback of each type
  return calls.map(c => {
    if (!c.traceCall?.summary?.weave?.feedback) {
      return c;
    }
    const feedback = c.traceCall?.summary?.weave?.feedback?.reduce(
      (acc: Record<string, any>, curr: Record<string, any>) => {
        // keep most recent feedback of each type
        if (acc[curr.feedback_type]?.created_at > curr.created_at) {
          return acc;
        }
        acc[curr.feedback_type] = curr;
        return acc;
      },
      {}
    );
    c.traceCall = {
      ...c.traceCall,
      summary: {
        ...c.traceCall.summary,
        weave: {
          ...c.traceCall.summary.weave,
          feedback,
        },
      },
    };
    return c;
  });
};

const CACHE_KEY_PREFIX = 'weave_datetime_filter_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day

const datetimeFilterCacheKey = (
  entity: string,
  project: string,
  filter: WFHighLevelCallFilter
) => {
  // hash the filter
  const filterHash = simpleHash(JSON.stringify(filter));
  return `${CACHE_KEY_PREFIX}${entity}_${project}_${filterHash}`;
};

export const useMakeInitialDatetimeFilter = (
  entity: string,
  project: string,
  highLevelFilter: WFHighLevelCallFilter,
  skip: boolean
): {initialDatetimeFilter: GridFilterModel} => {
  // Fire off 2 stats queries, one for the # of calls in the last 7 days
  // one for the  # of calls in the last 30 days.
  // If the first query returns > 50 calls, set the default filter to 7 days
  // Else if the second query returns > 50 calls, set to 30 days
  // Else set a default filter to 6 months
  // On the creation of a filter --> stash to browser history with 1 day expiry
  // Use the cache preferentially when available
  const {useCallsStats} = useWFHooks();
  const d30filter = useMemo(() => {
    return makeRawDateFilter(30);
  }, []);
  const d7filter = useMemo(() => {
    return makeRawDateFilter(7);
  }, []);

  const key = datetimeFilterCacheKey(entity, project, highLevelFilter);
  const cachedFilter = useMemo(
    () => getCachedByKeyWithExpiry(key, CACHE_EXPIRY_MS),
    [key]
  );
  const filter: CallFilter = useMemo(() => {
    return convertHighLevelFilterToLowLevelFilter(highLevelFilter);
  }, [highLevelFilter]);

  const callStats7Days = useCallsStats({
    entity,
    project,
    filter,
    query: d7filter,
    skip: skip || cachedFilter != null,
  });
  const callStats30Days = useCallsStats({
    entity,
    project,
    filter,
    query: d30filter,
    skip: skip || cachedFilter != null,
  });

  const defaultDatetimeFilter = useMemo(
    () => ({
      items: [makeDateFilter(7)],
      logicOperator: GridLogicOperator.And,
    }),
    []
  );

  const computedDatetimeFilter = useMemo(() => {
    // Wait for both stats queries to return
    if (callStats7Days.loading || callStats30Days.loading) {
      return defaultDatetimeFilter;
    }

    // If no cache or expired, compute new filter
    let newFilter = null;
    if (callStats7Days.result && callStats7Days.result.count >= 50) {
      newFilter = defaultDatetimeFilter;
    } else if (callStats30Days.result && callStats30Days.result.count >= 50) {
      newFilter = {
        items: [makeMonthFilter()],
        logicOperator: GridLogicOperator.And,
      };
    } else if (callStats30Days.result && callStats30Days.result.count < 50) {
      // If there are no calls found in the last 30 days, just don't set a filter
      newFilter = {
        items: [],
        logicOperator: GridLogicOperator.And,
      };
    }
    // Cache the new filter if we computed one
    if (newFilter) {
      setCacheByKeyWithExpiry(key, newFilter);
    }
    return newFilter;
  }, [callStats7Days, callStats30Days, defaultDatetimeFilter, key]);

  if (cachedFilter) {
    return {
      initialDatetimeFilter: cachedFilter as GridFilterModel,
    };
  }

  return {
    initialDatetimeFilter: computedDatetimeFilter ?? defaultDatetimeFilter,
  };
};
